'use client';

import { Toggle as TogglePrimitive } from '@base-ui/react/toggle';
import { cn } from '@zealot/ui/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';

// gloss
const toggleVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-lg text-sm font-medium transition-colors hover:bg-muted hover:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-transparent data-pressed:bg-accent data-pressed:text-accent-foreground data-pressed:hover:bg-accent data-pressed:hover:text-accent-foreground',
        outline:
          'border border-input bg-transparent shadow-sm hover:bg-accent hover:text-accent-foreground data-pressed:bg-accent data-pressed:text-accent-foreground data-pressed:hover:bg-accent data-pressed:hover:text-accent-foreground',
        primary:
          'bg-transparent data-pressed:bg-primary data-pressed:text-primary-foreground data-pressed:hover:bg-primary data-pressed:hover:text-primary-foreground',
        segmented:
          'relative rounded-md border border-transparent bg-transparent text-muted-foreground hover:bg-background/60 hover:text-foreground data-pressed:bg-background data-pressed:text-foreground data-pressed:shadow-sm data-pressed:hover:bg-background data-pressed:hover:text-foreground dark:data-pressed:border-input dark:data-pressed:bg-input/30 before:absolute before:inset-y-2 before:-left-px before:w-px before:bg-border first:before:hidden data-pressed:before:hidden [[data-pressed]+&]:before:hidden',
      },
      size: {
        sm: "h-7 px-1.5 min-w-7 [&_svg:not([class*='size-'])]:size-3.5",
        default: "h-9 px-2 min-w-9 [&_svg:not([class*='size-'])]:size-4",
        lg: "h-10 px-2.5 min-w-10 [&_svg:not([class*='size-'])]:size-4",
      },
    },
    compoundVariants: [
      { variant: 'segmented', size: 'sm', class: 'px-2.5' },
      { variant: 'segmented', size: 'default', class: 'px-3' },
      { variant: 'segmented', size: 'lg', class: 'px-4' },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

type ToggleProps = TogglePrimitive.Props &
  VariantProps<typeof toggleVariants> & {
    className?: string;
  };

function Toggle({ className, variant = 'default', size = 'default', ...props }: ToggleProps) {
  return <TogglePrimitive data-slot="toggle" className={cn(toggleVariants({ variant, size, className }))} {...props} />;
}

export { Toggle, toggleVariants };
